import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from '../../components/blocks/localization';
import { FilterTag } from '../../components/composites';
import { SearchableExtractionFields } from '../../redux/types';
import { ExtractionDisplayStatus } from '../../types';

interface ExtractionFilterState {
    statusFilter: ExtractionDisplayStatus[];
    dateRange: string[];
    query: string;
    searchBy: SearchableExtractionFields;
}

interface UseExtractionFiltersProps {
    initialState: ExtractionFilterState;
    onFilterChange: (filters: ExtractionFilterState) => void;
}

const filterTagKeyForField = (field: SearchableExtractionFields): string => {
    switch (field) {
        case SearchableExtractionFields.fileName:
            return 'filterTag-fileName';
        case SearchableExtractionFields.requestedBy:
            return 'filterTag-requestedBy';
        default:
            return 'filterTag-customerReference';
    }
};

/**
 * Holds the filter selections locally so the reseller can set several of them before the Search
 * button pushes them to redux, which is what triggers the list request.
 */
export const useExtractionFilters = ({ initialState, onFilterChange }: UseExtractionFiltersProps) => {
    const { t } = useTranslation(['extractionListPage', 'extractionStatus']);
    const [localStatusFilter, setLocalStatusFilter] = useState<ExtractionDisplayStatus[]>(initialState.statusFilter);
    const [localDateRange, setLocalDateRange] = useState<string[]>(initialState.dateRange);
    const [localQuery, setLocalQuery] = useState<string>(initialState.query);
    const [localSearchBy, setLocalSearchBy] = useState<SearchableExtractionFields>(initialState.searchBy);

    const filterTags: FilterTag[] = useMemo(
        () => [
            ...localStatusFilter.map((status) => ({
                label: t('filterTag-status', {
                    value: t('extractionStatus:extractionStatus-value', { context: status }),
                }),
                key: `status-${status}`,
                remove: () => setLocalStatusFilter((f) => f.filter((x) => x !== status)),
            })),
            ...(localQuery
                ? [
                      {
                          label: t(filterTagKeyForField(localSearchBy), { value: localQuery }),
                          key: 'query',
                          remove: () => setLocalQuery(''),
                      },
                  ]
                : []),
        ],
        [localStatusFilter, localQuery, localSearchBy, t]
    );

    const submitQuery = useCallback(() => {
        onFilterChange({
            statusFilter: localStatusFilter,
            dateRange: localDateRange,
            query: localQuery,
            searchBy: localSearchBy,
        });
    }, [onFilterChange, localStatusFilter, localDateRange, localQuery, localSearchBy]);

    const clearFilters = useCallback(() => {
        setLocalStatusFilter([]);
        setLocalDateRange([]);
        setLocalQuery('');
    }, []);

    return {
        filters: {
            status: { value: localStatusFilter, onChange: setLocalStatusFilter },
            dateRange: { value: localDateRange, onChange: setLocalDateRange },
            query: { value: localQuery, onChange: setLocalQuery },
            searchBy: { value: localSearchBy, onChange: setLocalSearchBy },
        },
        filterTags,
        submitQuery,
        clearFilters,
    };
};
